import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GenericDataProvider from '../../common/api/GenericDataProvider';
import calculateDynamicPageSize from '../common/calculateDynamicPageSize';
import { parseSearchText, Pagination } from './useData';
import { AdminModelMap, AdminObjectName } from '../common/DataColumnConfig';
import log from '../../common/utils/logger';

const EMPTY_ARRAY: unknown[] = [];

interface UseLinkedDataProps<K extends AdminObjectName> {
  parentObjectName: string;
  parentId: string;
  objectName: K;
  filter?: Record<string, unknown>;
  sort?: string;
  searchText?: string | null;
  defaultSearchField?: string;
  searchableFields?: string[];
  currentPage: number;
  currentPageSize?: number;
}

const useLinkedData = <K extends AdminObjectName, T = AdminModelMap[K]>({
  parentObjectName,
  parentId,
  objectName,
  filter = {},
  sort,
  searchText,
  defaultSearchField,
  searchableFields,
  currentPage,
  currentPageSize,
}: UseLinkedDataProps<K>) => {
  const dataProvider = new GenericDataProvider();
  const queryClient = useQueryClient();
  const hasExtraFooter = true;

  const effectivePageSize = React.useMemo(
    () => currentPageSize || calculateDynamicPageSize(hasExtraFooter),
    [currentPageSize, hasExtraFooter],
  );

  const searchInfo = React.useMemo(
    () => parseSearchText({
      defaultSearchField: defaultSearchField || 'title',
      searchableFields,
      searchText,
    }),
    [defaultSearchField, searchableFields, searchText],
  );

  const fetchData = async ({ queryKey: key }: { queryKey: unknown[] }) => {
    const [, , , { page = 1, sort: sortVal } = {}] = key as [string, string, string, { page?: number; sort?: string }];

    const options = {
      page,
      pageSize: effectivePageSize,
      sort: sortVal,
      filter: { ...filter, ...searchInfo?.filters },
    };
    const result = await dataProvider.getLinkedItems(parentObjectName, parentId, objectName, options);

    return {
      ...result,
      pagination: {
        ...result.pagination,
        pageSize: effectivePageSize,
        total: result.pagination?.total || 0,
        page,
      },
    };
  };

  const queryKey = React.useMemo(
    () => [
      `/${parentObjectName}`,
      parentId,
      objectName,
      {
        page: currentPage,
        pageSize: effectivePageSize,
        search: searchText,
        sort,
      },
    ],
    [parentObjectName, parentId, objectName, currentPage, effectivePageSize, searchText, sort],
  );

  const {
    data, isLoading, isPlaceholderData: isPreviousData, error,
  } = useQuery({
    queryKey,
    queryFn: fetchData,
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  });

  const linkMutation = useMutation({
    mutationFn: (childId: string | number) => dataProvider.linkItems(parentObjectName, parentId, objectName, childId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/${parentObjectName}`, parentId, objectName],
      });
    },
    onError: (err) => {
      log.info('Error linking items:', err);
    },
  });

  const updateLinkMutation = useMutation({
    mutationFn: ({ id, data: newData }: { id: string | number; data: unknown }) => (
      dataProvider.updateLink(parentObjectName, parentId, objectName, id, newData)
    ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey);
      return { previousData: prevData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      log.error('Error updating link:', err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [`/${parentObjectName}`, parentId, objectName],
      });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (childId: string | number) => dataProvider.unlinkItems(parentObjectName, parentId, objectName, childId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/${parentObjectName}`, parentId, objectName],
      });
    },
  });

  // Prefer previous pagination while refetching or when current payload has no totals
  const cached = queryClient.getQueryData(queryKey) as { pagination?: Pagination } | undefined;
  const safePagination = data?.pagination
    && typeof data.pagination.total === 'number'
    && !isLoading
    && !isPreviousData
    ? (data.pagination as Pagination)
    : (cached?.pagination as Pagination | undefined);

  const memoizedPagination = React.useMemo(
    () => safePagination || { page: currentPage, pageSize: effectivePageSize, total: 0 },
    [safePagination, currentPage, effectivePageSize],
  );

  return {
    data: (data?.data || EMPTY_ARRAY) as T[],
    isLoading,
    isPreviousData: !!isPreviousData,
    error,
    pagination: memoizedPagination,
    linkMutation,
    unlinkMutation,
    updateLinkMutation,
  };
};

export default useLinkedData;
